import { Box, Button, Checkbox, FormControlLabel, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { MdAdd, MdDeleteOutline } from 'react-icons/md';

// Rows of { _id?, date: 'YYYY-MM-DD', time, sold } for a course offered on
// several dates. Parents pick one when booking; each date can be marked
// sold out on its own. Used by NewCourse and UpdateCourse.

export const emptyDateRow = () => ({ date: '', time: '', sold: false });

// API date (ISO string) → value for <input type="date">, in local time.
export const toDateInput = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Problem with the rows as a sentence, or null if they can be saved.
export const datesProblem = (rows) => {
    const filled = rows.filter((r) => r.date);
    if (filled.length < 2) return 'Add at least two dates, or untick "Offer this course on several dates"';
    if (filled.length !== rows.length) return 'Every date row needs a date (or remove the empty row)';
    if (new Set(filled.map((r) => r.date)).size !== filled.length) return 'The same date is listed twice';
    return null;
};

const CourseDatesEditor = ({ rows, onChange }) => {
    const update = (index, field, value) => onChange(rows.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
    const remove = (index) => onChange(rows.filter((_, i) => i !== index));

    // Inline width: the course forms override direct children's width via
    // '& > :not(style)', which would otherwise squeeze this into one column.
    return (
        <Box style={{ width: '100%' }} sx={{ mt: 1, mx: '10px' }}>
            <Typography variant="body2" sx={{ fontFamily: 'Inter', color: 'text.secondary', mb: 1 }}>
                Parents choose one of these dates when booking. Leave a time empty to use the course time.
                Past dates are hidden from parents automatically.
            </Typography>
            <Stack spacing={1.5}>
                {rows.map((row, i) => (
                    <Stack key={row._id || `new-${i}`} direction="row" spacing={1.5} alignItems="center">
                        <TextField
                            type="date"
                            label={`Date ${i + 1}`}
                            value={row.date}
                            onChange={(e) => update(i, 'date', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                            sx={{ width: 180 }}
                        />
                        <TextField
                            label="Time (optional)"
                            value={row.time}
                            onChange={(e) => update(i, 'time', e.target.value)}
                            size="small"
                            sx={{ width: 180 }}
                        />
                        <FormControlLabel
                            control={<Checkbox checked={!!row.sold} onChange={(e) => update(i, 'sold', e.target.checked)} />}
                            label="Sold out"
                        />
                        <Tooltip title="Remove this date">
                            <IconButton onClick={() => remove(i)} aria-label={`Remove date ${i + 1}`}>
                                <MdDeleteOutline />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                ))}
            </Stack>
            <Button startIcon={<MdAdd />} onClick={() => onChange([...rows, emptyDateRow()])} sx={{ mt: 1 }}>
                Add date
            </Button>
        </Box>
    );
};

export default CourseDatesEditor;
